// Carrier features — Detection Engine System 1 (spec §2.2).
//
// Работают на несущей `c` (красная линия после свёртки main pattern, §2.1).
// `c` может быть дробной (group mean / силуэт) → ε переключается (§2.2):
//   single real line → ε = 1 (целочисленные слайдеры)
//   fractional carrier → ε = 0.5
// Амплитудные критерии (A, Net) считаются на вещественной кривой без ε.

import { THRESHOLDS, SPANS } from './thresholds.js';

/**
 * Полный набор признаков несущей.
 * @param {number[]} c   carrier-массив, длина N ≥ 1
 * @param {boolean} fractional  дробная несущая (group mean / силуэт)
 */
export function carrierFeatures(c, fractional = false) {
  const N = c.length;
  const eps = fractional ? THRESHOLDS.EPS_FRACTIONAL : THRESHOLDS.EPS_INTEGER;

  const deltas = [];
  for (let i = 0; i < N - 1; i++) deltas.push(c[i + 1] - c[i]);

  const steps = deltas.map((d) => {
    if (d >= eps) return 'up';
    if (d <= -eps) return 'down';
    return 'flat';
  });

  const amplitude = N ? Math.max(...c) - Math.min(...c) : 0;
  const net = N ? c[N - 1] - c[0] : 0;

  const { extremaMax, extremaMin } = interiorExtrema(c);

  const segments = buildSegments(steps);
  const directed = segments.filter((s) => s.dir !== 'hold').map((s) => s.dir);

  return {
    c,
    N,
    eps,
    fractional,
    deltas,
    steps,
    amplitude,
    net,
    segments,
    directedSegments: directed, // S = [up|down …], holds absorbed
    extremaMax, // позиции локальных max (0-based, interior)
    extremaMin, // позиции локальных min (0-based, interior)
    eMax: extremaMax.length,
    eMin: extremaMin.length,
    maxHoldStages: maxHold(segments),
    spans: {
      early: SPANS.early(N), // # переходов в начале
      late: SPANS.late(N),
      majoritySpan: SPANS.majoritySpan(N),
      holdMin: SPANS.holdMin(N), // в ЭТАПАХ
    },
  };
}

/** Локальные экстремумы на внутренних индексах 1..N−2 (строгие, как прототип). */
export function interiorExtrema(c) {
  const extremaMax = [];
  const extremaMin = [];
  for (let i = 1; i < c.length - 1; i++) {
    if (c[i] > c[i - 1] && c[i] > c[i + 1]) extremaMax.push(i);
    else if (c[i] < c[i - 1] && c[i] < c[i + 1]) extremaMin.push(i);
  }
  return { extremaMax, extremaMin };
}

/**
 * Сегменты (§2.2): свернуть подряд одинаково-направленные шаги; flat внутри
 * направления поглощается (фиксируется как hold); чистый run из flat = hold.
 *
 * Правило поглощения: maximal-run из flat, ограниченный с ОБЕИХ сторон одним и
 * тем же направлением, сливается в это направление. Flat-run на краю или между
 * разными направлениями → отдельный hold-сегмент.
 *
 * Каждый сегмент: { dir: 'up'|'down'|'hold', start, end, length, stages }
 *   length — число шагов; stages — число этапов (length+1).
 */
export function buildSegments(steps) {
  if (steps.length === 0) return [];

  // 1. Maximal-runs одинакового step-type.
  const runs = [];
  for (let i = 0; i < steps.length; i++) {
    const last = runs[runs.length - 1];
    if (last && last.type === steps[i]) {
      last.end = i;
    } else {
      runs.push({ type: steps[i], start: i, end: i });
    }
  }

  // 2. Поглощение flat-runs, обрамлённых одинаковым направлением.
  const merged = [];
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i];
    if (r.type === 'flat') {
      const prev = runs[i - 1];
      const next = runs[i + 1];
      if (prev && next && prev.type === next.type && prev.type !== 'flat') {
        // flat внутри направления → расширяем предыдущий directed-run,
        // помечаем next к слиянию.
        const target = merged[merged.length - 1];
        target.end = next.end;
        runs[i + 1] = { ...next, _absorbed: true };
        continue;
      }
      merged.push({ ...r });
    } else if (r._absorbed) {
      continue;
    } else {
      merged.push({ ...r });
    }
  }

  return merged.map((r) => ({
    dir: r.type === 'flat' ? 'hold' : r.type,
    start: r.start,
    end: r.end,
    length: r.end - r.start + 1,
    stages: r.end - r.start + 2,
  }));
}

/** Самый длинный hold-сегмент в ЭТАПАХ (для cliff/rise/plateau). */
function maxHold(segments) {
  let best = 0;
  for (const s of segments) {
    if (s.dir === 'hold' && s.stages > best) best = s.stages;
  }
  return best;
}

/** Сумма понижений в окне переходов [from, to). */
export function sumDownIn(deltas, from, to) {
  let s = 0;
  for (let i = from; i < to && i < deltas.length; i++) {
    if (deltas[i] < 0) s += -deltas[i];
  }
  return s;
}

/** Сумма повышений в окне переходов [from, to). */
export function sumUpIn(deltas, from, to) {
  let s = 0;
  for (let i = from; i < to && i < deltas.length; i++) {
    if (deltas[i] > 0) s += deltas[i];
  }
  return s;
}
